/**
 * The two silhouettes, front and back, with what the movement trains lit on them.
 *
 * ── Monochrome, on the grey steps, with no ring ─────────────────────────────────────────────
 * The plan library's inspector figures, at this screen's own size. Primary muscles take
 * `--silhouette-strong`, secondary `--silhouette-mid`, everything else `--silhouette-base`. Colour
 * on this screen means TYPE and nothing else, so a body painted in the fourteen muscle hues would
 * be a second colour vocabulary answering a question nobody asked.
 *
 * ── `gender="male"`, constant, and it is a recorded deviation ───────────────────────────────
 * An exercise has no person. `BodySilhouette` needs one because its artwork has two sets, and the
 * specimen makes the same choice. Entry 4 in `kanon-cwiczenia-2026-09-17/README.md`.
 *
 * ── The figures are `aria-hidden` and the muscles are PILLS ─────────────────────────────────
 * A drawing cannot be read, and the pills under it name every group in words — so a listener gets
 * the whole answer once, as text, rather than a `role="img"` whose label repeats what the pills
 * beside it already say.
 *
 * ── The THEATRE draws no PRZÓD / TYŁ captions, and the panel does ────────────────────────────
 * Read off the two frozen canons rather than chosen: `kanon-01-lista-*.png` captions its 190px pair
 * and `kanon-02-teatr-*.png` does not caption its 230px one. It reads as the same decision on both
 * — at 190px inside a 396px column the two silhouettes are small and near each other and the words
 * do real work; at 230px with a whole rail around them the front and the back are unmistakable, and
 * two grey words under a drawing would be labelling the obvious. The captions carry no information
 * a listener needs either way: the whole figure is `aria-hidden`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REGIONS } from '~/shared/ui/bodyModel/regions';
import { BodySilhouette } from '~/shared/ui/bodyModel/BodySilhouette';
import type { BodySlug } from '~/shared/ui/bodyModel/bodyArtwork';
import { ALL_MUSCLE_GROUPS, muscleGroupToApi, type MuscleGroup } from '../model/muscleGroup';
import { lightsWholeBody, muscleGroupOfSlug, slugsOfMuscleGroup } from '../model/muscleSlugs';

import styles from './MuscleFigures.module.css';

const SIDES = [
  { side: 'front', labelKey: 'clients.detail.muscleMap.front' },
  { side: 'back', labelKey: 'clients.detail.muscleMap.back' },
] as const;

/** Every shape on each view that some group paints — the shapes a press can mean something on. */
const PRESSABLE: Record<'front' | 'back', ReadonlySet<BodySlug>> = {
  front: new Set(ALL_MUSCLE_GROUPS.flatMap((group) => slugsOfMuscleGroup(group, 'front'))),
  back: new Set(ALL_MUSCLE_GROUPS.flatMap((group) => slugsOfMuscleGroup(group, 'back'))),
};

interface MuscleFiguresProps {
  readonly primary: readonly MuscleGroup[];
  readonly secondary: readonly MuscleGroup[];
  /**
   * The theatre draws a taller pair than the panel does, and the creator a shorter one beside its
   * chip grid. All three read the same fills; only the height and the captions differ.
   */
  readonly size?: 'panel' | 'theatre' | 'creator';
  /**
   * Makes the figure a way to CHOOSE a muscle (the creator's D21): a press on a shape reports its
   * group, and a hover lights that group's shapes one grey step up. The figure stays `aria-hidden`
   * — the caller's chips are the keyboard and screen-reader path to the same act.
   */
  readonly onPressMuscle?: (group: MuscleGroup) => void;
}

/**
 * Which shape each group paints ON ONE VIEW, as a map that view's figure can read.
 *
 * ── Per SIDE, and that is the whole reason this takes a side at all ─────────────────────────
 * `REGIONS` names both a slug AND a view — `Shoulders` is the FRONT deltoid and the back view's own
 * deltoid stays neutral, which is what the phone draws and what `regions.ts` says in its own header.
 * `BodySilhouette` takes a slug map with no notion of a side, so a single map handed to both
 * figures lights every shape that happens to share a name: the specimen's back silhouette has grey
 * shoulders and ours had lit ones until this was split. The filter is the fix.
 *
 * `REGIONS` is keyed by the CONTRACT's spelling, so the slug goes back through `muscleGroupToApi`
 * rather than being looked up by this slice's own lower-cased name — the one seam that is the
 * authority on what a muscle group is called.
 *
 * Secondary first, then primary: a group named in both lists is the PRIMARY one, and painting in
 * that order lets the stronger fill win without a predicate saying so. `Other` and `FullBody` have
 * no anatomy in `REGIONS` and light nothing here; a movement whose ONLY group is `FullBody` is lit
 * whole through the base fill instead (`lightsWholeBody`).
 */
function muscleFills(
  primary: readonly MuscleGroup[],
  secondary: readonly MuscleGroup[],
  side: 'front' | 'back',
): Readonly<Partial<Record<BodySlug, string>>> {
  const fills: Partial<Record<BodySlug, string>> = {};
  const paint = (groups: readonly MuscleGroup[], ink: string) => {
    for (const group of groups) {
      // `Object.hasOwn` rather than a bare index: a bare one reaches the prototype chain, so a name
      // like `constructor` would resolve to a FUNCTION, which is not nullish and would be iterated.
      const wire = muscleGroupToApi(group);
      if (!Object.hasOwn(REGIONS, wire)) continue;
      for (const region of REGIONS[wire] ?? []) {
        if (region.side === side) fills[region.muscle] = ink;
      }
    }
  };
  paint(secondary, 'var(--silhouette-mid)');
  paint(primary, 'var(--silhouette-strong)');
  return fills;
}

export function MuscleFigures({ primary, secondary, size, onPressMuscle }: MuscleFiguresProps) {
  const { t } = useTranslation();
  const baseFill = lightsWholeBody(primary, secondary) ? 'var(--silhouette-strong)' : 'var(--silhouette-base)';
  const [hovered, setHovered] = useState<MuscleGroup | null>(null);

  /** The fills, with the hovered group one grey step up where it is not already chosen. */
  const fillsFor = (side: 'front' | 'back') => {
    const fills = muscleFills(primary, secondary, side);
    if (hovered === null || primary.includes(hovered) || secondary.includes(hovered)) return fills;
    const lit: Partial<Record<BodySlug, string>> = { ...fills };
    for (const slug of slugsOfMuscleGroup(hovered, side)) lit[slug] = 'var(--silhouette-weak)';
    return lit;
  };

  return (
    <span
      className={`${String(styles.figures)} ${size === undefined || size === 'panel' ? '' : String(styles[size])}`}
      aria-hidden
    >
      {SIDES.map(({ side, labelKey }) => (
        <span key={side} className={styles.box}>
          <BodySilhouette
            side={side}
            gender="male"
            fillBySlug={fillsFor(side)}
            baseFill={baseFill}
            interactive={
              onPressMuscle === undefined
                ? undefined
                : {
                    pressable: PRESSABLE[side],
                    onPress: (slug) => {
                      const group = muscleGroupOfSlug(slug, side);
                      if (group !== null) onPressMuscle(group);
                    },
                    onHover: (slug) => {
                      setHovered(slug === null ? null : muscleGroupOfSlug(slug, side));
                    },
                  }
            }
          />
          {size !== 'theatre' && <span className={styles.caption}>{t(labelKey)}</span>}
        </span>
      ))}
    </span>
  );
}
